import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Crossfading photo gallery that auto-advances. Falls back to `fallback`
// (e.g. a gradient/placeholder) when there are no images.
export default function AutoGallery({
  images = [],
  interval = 3500,
  heightClass = "h-full",
  rounded = "",
  alt = "",
  showDots = true,
  interactiveDots = true,
  fallback = null,
}) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const base = process.env.REACT_APP_BASE_API;

  useEffect(() => {
    if (images.length <= 1) return;
    // Автосмена кадров — это движущийся контент: людям с вестибулярными
    // расстройствами он мешает, и WCAG 2.2.2 требует его останавливать.
    // Лента туров это уже учитывала, галерея — нет.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(
      () => setIdx((i) => (i + 1) % images.length),
      interval
    );
    return () => clearInterval(id);
  }, [images.length, interval]);

  useEffect(() => {
    if (idx >= images.length) setIdx(0);
  }, [images.length, idx]);

  if (!images || images.length === 0) return fallback;

  return (
    <div className={`relative w-full ${heightClass} overflow-hidden ${rounded}`}>
      {images.map((url, i) => (
        <img
          key={`${url}-${i}`}
          src={`${base}${url}`}
          // Описание получает только видимый кадр. Раньше один и тот же alt
          // стоял на всех пяти фото сразу — скринридер зачитывал название тура
          // пять раз подряд, а невидимые кадры всё равно объявлялись.
          alt={i === idx ? alt : ""}
          aria-hidden={i === idx ? undefined : "true"}
          loading={i === 0 ? "eager" : "lazy"}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            i === idx ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}

      {showDots && images.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
          {images.map((_, i) =>
            interactiveDots ? (
              <button
                key={i}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIdx(i);
                }}
                // Зона нажатия 44px вокруг точки: сама точка остаётся 6px,
                // но попасть по ней пальцем теперь реально.
                className="group/dot flex h-11 w-6 items-center justify-center focus-visible:outline-none"
                aria-label={t("gallery.goToPhoto", { number: i + 1 })}
                aria-current={i === idx ? "true" : undefined}
              >
                <span
                  className={`h-1.5 rounded-full transition-all group-focus-visible/dot:ring-2 group-focus-visible/dot:ring-kj-gold ${
                    i === idx ? "w-5 bg-white" : "w-1.5 bg-white/60 group-hover/dot:bg-white/90"
                  }`}
                />
              </button>
            ) : (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-5 bg-white" : "w-1.5 bg-white/50"
                }`}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}

// Resolve the image list for an activity (new `images` array, or legacy single).
export const tourImages = (activity) => {
  if (activity?.images && activity.images.length) return activity.images;
  if (activity?.imageUrl) return [activity.imageUrl];
  return [];
};
